// src/pages/ManagementDashboard/ui/OrganizationIsland.jsx
import React, { useEffect, useState } from "react";
import { Briefcase, Users, CalendarClock, AlertCircleIcon, MoreHorizontal } from "lucide-react";
import { getOrgSummary, getOrganizationMembers, updateOrganizationMemberRole } from "../../../../api/organizationApi";
import "./OrganizationIsland.css";
import DashboardIsland from "../Dashboard/DashboardIsland";
import Dropdown from "../../../../components/Dropdown/Dropdown";
import Pagination from "../../../../components/Pagination/Pagination";
import TableScroll from "../../../../components/TableScroll/TableScroll";

const ROLE_OPTIONS = [
  {
    value: "admin",
    label: "Admin",
    help: "Can manage the org: create groups, invite members, and assign meetings.",
  },
  {
    value: "member",
    label: "Member",
    help: "Can take assigned meetings only. No dashboard access and cannot manage groups.",
  },
];

function Modal({ open, title, onClose, children }) {
  if (!open) return null;
  return (
    <div className="gd-modal-backdrop" onMouseDown={onClose}>
      <div className="gd-modal" onMouseDown={(e) => e.stopPropagation()}>
        <div className="gd-modal-header">
          <div className="gd-modal-title">{title}</div>
          <button className="gd-icon-btn" onClick={onClose} aria-label="Close">✕</button>
        </div>
        <div className="gd-modal-body">{children}</div>
      </div>
    </div>
  );
}

function RowActionsMenu({ onEditPermissions, canEdit }) {
  return (
    <div
      className="gd-row-actions-menu"
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
    >
      <button
        className="gd-menu-item"
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onEditPermissions();
        }}
        disabled={!canEdit}
        title={!canEdit ? "You can’t edit your own permissions" : "Edit permissions"}
      >
        Edit permissions
      </button>
    </div>
  );
}

function roleLabel(role) {
  return role === "admin" ? "Admin" : "Member";
}



export default function OrganizationIsland({ organizationId, me }) {
  const [data, setData] = useState(null);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [membersLoading, setMembersLoading] = useState(false);
  const [error, setError] = useState(null);
  const [membersError, setMembersError] = useState(null);
  // role UI state
  const [pendingRole, setPendingRole] = useState({}); // { [userId]: "admin" | "member" }
  const PAGE_SIZE = 10;
  const [memberPage, setMemberPage] = useState(1);
  const [memberPages, setMemberPages] = useState(1);
  const [rowMenuOpenFor, setRowMenuOpenFor] = useState(null);

  // permissions modal state
  const [permOpen, setPermOpen] = useState(false);
  const [permMember, setPermMember] = useState(null);
  const [permRole, setPermRole] = useState("member");
  const [permSaving, setPermSaving] = useState(false);
  const [permError, setPermError] = useState("");

  useEffect(() => {
    let alive = true;

    async function load() {
      if (!organizationId) {
        setLoading(false);
        setData(null);
        setMembers([]);
        return;
      }

      setLoading(true);
      setError(null);
      setMembersError(null);

      try {
        const summary = await getOrgSummary(organizationId);
        if (!alive) return;
        setData(summary);

        // members (admin-only)
        setMembersLoading(true);
        const resp = await getOrganizationMembers(organizationId, {
          page: memberPage,
          pageSize: PAGE_SIZE,
        });

        if (!alive) return;

        const items = Array.isArray(resp?.items) ? resp.items : [];
        setMembers(items);
        setMemberPages(Number(resp?.pages) || 1);

        // keep UI in sync if backend clamped the page
        if (resp?.page && resp.page !== memberPage) setMemberPage(resp.page);

        const init = {};
        items.forEach(m => { init[m.id] = m.role; });
        setPendingRole(init);

      } catch (e) {
        if (!alive) return;
        console.error("org load failed:", e);
        setError(e);
        setData(null);
        setMembers([]);
      } finally {
        if (!alive) return;
        setLoading(false);
        setMembersLoading(false);
      }
    }

    load();
    return () => { alive = false; };
  }, [organizationId, memberPage]);

  useEffect(() => {
    function onDocClick(e) {
      if (!e.target.closest(".org-row-actions")) setRowMenuOpenFor(null);
    }
    document.addEventListener("click", onDocClick);
    return () => document.removeEventListener("click", onDocClick);
  }, []);

  const isSelf = (userId) => {
 return me === userId ? true : false
  }

  const openPermModal = (m) => {
    if (!m || isSelf(m.id)) return;  
    setPermError("");
    setPermMember(m);
    setPermRole(m?.role || "member");
    setPermOpen(true);
    setRowMenuOpenFor(null);
  };

  const closePermModal = () => {
    setPermOpen(false);
    setPermMember(null);
    setPermError("");
  };

  const savePermRole = async () => {
    if (!permMember) return;

    const userId = permMember.id;
    const existing = permMember.role;
    const next = permRole;

    if (!next || next === existing) return;

    // optional: keep your “no self change” rule
    if (isSelf(userId)) {
      setPermError("You can’t change your own role here.");
      return;
    }

    setPermSaving(true);
    setPermError("");

    try {
      const resp = await updateOrganizationMemberRole(organizationId, userId, next);
      const updatedRole = resp?.member?.role ?? next;

      // update local list immediately
      setMembers((prev) =>
        prev.map((m) => (Number(m.id) === Number(userId) ? { ...m, role: updatedRole } : m))
      );

      // keep modal snapshot accurate
      setPermMember((prev) => (prev ? { ...prev, role: updatedRole } : prev));

      setPermOpen(false);
    } catch (e) {
      const msg =
        e?.response?.data?.error ||
        e?.response?.data?.message ||
        e?.message ||
        "Failed to update role";
      setPermError(msg);
    } finally {
      setPermSaving(false);
    }
  };

  return (
    <DashboardIsland title="Organization" icon={<Briefcase size={20} color="#082f49" />}>
      {/* Loading */}
      {loading && (
        <div className="org-skeleton">
          <div className="org-skel-title" />
          <div className="org-skel-row">
            <div className="org-skel-chip" />
            <div className="org-skel-chip" />
          </div>
        </div>
      )}

      {/* No org selected */}
      {!loading && !organizationId && <div className="ov-empty">No organization selected.</div>}

      {/* Error */}
      {!loading && error && <div className="ov-empty">Couldn’t load organization summary.</div>}

      {/* Data */}
      {!loading && organizationId && data && (
        <div className="org-wrap">
          <div className="org-head">
            <div className="org-title-row">
              <h3 className="org-title">{data.name || "—"}</h3>
              {data.industry && <span className="org-pill">{data.industry}</span>}
            </div>
          </div>

          <div className="org-stats">
            <div className="org-stat">
              <div className="org-stat-label">   <CalendarClock size={14} />Created</div>
              <div className="org-stat-value">
                {data.created_at ? new Date(data.created_at).toLocaleDateString() : "—"}
              </div>
            </div>
            <div className="org-stat">
              <div className="org-stat-label">              <Users size={16} />Members</div>
              <div className="org-stat-value">{data.members ?? 0}</div>
            </div>
          </div>

          {/* ✅ Members table */}
          <div style={{ marginTop: 16 }}>
            <div className="org-members-head">
              <div className="org-members-title">Members</div>
            </div>

            {membersLoading && <div className="ov-empty">Loading members…</div>}
            {!membersLoading && membersError && <div className="ov-empty">{membersError}</div>}

            {!membersLoading && !membersError && (
              <>
              <TableScroll >
                  <div className="org-members-table">
                  <div className="org-members-row org-members-head-row">
                    <div>User</div>
                    <div>Email</div>
                    <div style={{ width: 260 }}>Role</div>
                    <div style={{ width: 120 }}></div>
                  </div>

                  {members.map((m) => {
                    const isMenuOpen = rowMenuOpenFor === m.id;
                    const canEdit = !isSelf(m.id); // keep same rule
                    return (
                      <div className="org-members-row" key={m.id}>
                        <div className="org-members-user">
                          <div className="org-members-name">
                            {(m.first_name || m.last_name)
                              ? `${m.first_name || ""} ${m.last_name || ""}`.trim()
                              : "—"}
                          </div>
                        </div>

                        <div className="org-members-email">{m.email}</div>

                        {/* ✅ Read-only role pill */}
                        <div>
                          <span className={`gd-role-pill ${m.role === "admin" ? "admin" : "member"}`}>
                            {roleLabel(m.role)}
                          </span>
                        </div>

                        {/* ✅ 3-dot actions */}
                        <div className="org-row-actions" style={{ display: "flex", justifyContent: "flex-end" }}>
                          <button
                            className="gd-kebab"
                            disabled={!canEdit}
                            type="button"
                            aria-label="Member actions"
                            onClick={(e) => {
                              e.stopPropagation();
      
                              setRowMenuOpenFor((prev) => (prev === m.id ? null : m.id));
                            }}
                          >
                        
                            <MoreHorizontal size={24} />
                          </button>
                          {canEdit && isMenuOpen && (
                            <RowActionsMenu
                              canEdit={canEdit}
                              onEditPermissions={() => openPermModal(m)}
                            />
                          )}
                        </div>
                      </div>
                    );
                  })}


                  {members.length === 0 && (
                    <div className="ov-empty" style={{ padding: 14 }}>
                      No members found.
                    </div>
                  )}
                </div>
              </TableScroll>
              

                <div className="userprofile-pagination">
                  <Pagination page={memberPage} pages={memberPages} onChange={setMemberPage} />
                </div>

              </>

            )}
          </div>

          <Modal
  open={permOpen}
  title={`Edit permissions${permMember ? ` — ${(permMember.first_name || permMember.last_name)
    ? `${permMember.first_name || ""} ${permMember.last_name || ""}`.trim()
    : permMember.email}` : ""}`}
  onClose={closePermModal}
>
  <div className="gd-muted" style={{ marginBottom: 10 }}>
    Choose an organization role for this member.
  </div>

  <Dropdown
    options={ROLE_OPTIONS}
    value={permRole}
    onChange={(role) => setPermRole(role)}
    disabled={permSaving || (permMember && isSelf(permMember.id))}
    placeholder="Select role…"
    ariaLabel="Organization role"
  />

  {permMember && isSelf(permMember.id) && (
    <div className="gd-row-inline-help" style={{ marginTop: 8 }}>
      You can’t change your own role here.
    </div>
  )}

  {permError && (
    <div className="gd-error" style={{ marginTop: 10 }}>
      <AlertCircleIcon size={16} />
      <span>{permError}</span>
    </div>
  )}

  <div className="gd-modal-actions" style={{ marginTop: 14 }}>
    <button className="gd-secondary" onClick={closePermModal} type="button">
      Cancel
    </button>

    <button
      className="gd-primary"
      onClick={savePermRole}
      disabled={
        permSaving ||
        !permMember ||
        permRole === (permMember?.role || "member") ||
        (permMember && isSelf(permMember.id))
      }
      type="button"
    >
      {permSaving ? "Saving…" : "Save changes"}
    </button>
  </div>
</Modal>

        </div>
      )}
    </DashboardIsland>
  );
}
